import { AccordionItem, Alert, Heading } from '@mm/components';
import { CloseCircleIcon } from '@mm/icons';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { hideSlideoutMenu } from '@mm/state/actions/viewActions';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { SlideoutMenu } from '../../../../editor/components/SlideoutMenu/components/SlideoutMenu';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { useDetectOutsideClicks } from '../../../hooks/use-detect-outside-clicks';
import { SearchInput } from '../../SearchInput/SearchInput';
import {
  AddWidgetProps,
  QQ_SPLIT_ALERT,
  WidgetAddItem,
  WidgetDefinition,
} from '../AddWidgetInsert';
import './WidgetAdderSlideoutMenu.pcss';

type Props = {
  className?: string;
  mmWidgets?: WidgetDefinition[];
  connectionWidgets?: WidgetDefinition[];
  onSlideoutClose?: () => {};
};

export const WidgetAdderSlideoutMenu = (props: Props & AddWidgetProps) => {
  const {
    mmWidgets = [],
    connectionWidgets = [],
    className,
    onSlideoutClose = () => {},
    ...rest
  } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();
  const { updateUserAttributes } = useUsersCRUDActions();
  const { userAttributes } = useSelector(selectSessionSelector);
  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);

  const onCloseClick = () => {
    dispatch(hideSlideoutMenu());
    onSlideoutClose();
  };
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);

  useDetectOutsideClicks(() => {
    onCloseClick();
  }, wrapperRef);

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };

  const [search, onSearchChange] = useState('');

  const mmWidgetsFiltered = search
    ? mmWidgets.filter(w => w.name.toLowerCase().includes(search.toLowerCase()))
    : mmWidgets;

  const connectionWidgetsFiltered = search
    ? connectionWidgets.filter(w =>
        w.name.toLowerCase().includes(search.toLowerCase()),
      )
    : connectionWidgets;

  return (
    <SlideoutMenu
      className={classNames(className, 'widget-adder-slideout-menu')}
      setRef={setRef}
    >
      <SlideoutMenu.Content className="widget-adder-slideout-menu__content">
        <span
          className="widget-adder-slideout-menu__close-btn"
          onClick={onCloseClick}
        >
          <CloseCircleIcon
            className="widget-adder-slideout-menu__close-icon"
            color={colors.palePurple}
          />
        </span>

        <Heading size={2}>Widgets</Heading>

        <SearchInput
          className="widget-adder-slideout-menu__search"
          placeholder="Search Widgets"
          onChange={e => onSearchChange(e.target.value)}
          value={search}
        />

        {!userAttributes.seenQqSplitAlert && (
          <Alert
            className="widget-adder-slideout-menu__qq-alert"
            type="info"
            closeIconProps={{
              onClick: () =>
                updateUserAttributes(companyId, currentUserId, {
                  seenQqSplitAlert: true,
                }),
            }}
          >
            {QQ_SPLIT_ALERT}
          </Alert>
        )}

        <AccordionItem
          className="widget-adder-slideout-menu__widgets-accordion"
          title="Customers.ai Widgets"
          withBorder={false}
          closeOnOutsideClick={false}
          arrowDirectionVertical
          openOnMount
        >
          <div className="widget-adder-slideout-menu__widgets-list">
            {mmWidgetsFiltered.map(widget => (
              <WidgetAddItem
                key={widget.type + widget.name}
                onClickEnd={onCloseClick}
                withChannelValidation
                {...rest}
                {...widget}
              />
            ))}
          </div>
        </AccordionItem>
        <AccordionItem
          className="widget-adder-slideout-menu__widgets-accordion"
          title="Connection Widgets"
          withBorder={false}
          closeOnOutsideClick={false}
          arrowDirectionVertical
          openOnMount
        >
          <div className="widget-adder-slideout-menu__widgets-list">
            {connectionWidgetsFiltered.map(widget => (
              <WidgetAddItem
                key={widget.type + widget.name}
                onClickEnd={onCloseClick}
                iconOnly={widget.name !== 'Zap/ Connection'}
                {...rest}
                {...widget}
              />
            ))}
          </div>
        </AccordionItem>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
